/**
 * 先画到半精度浮点缓冲，再做一次保色相的色调映射。
 *
 * 一天几百张照片时，星团在远处挤在几个像素里，加法混合会叠出远大于 1 的值。
 * 直接输出会冲成一片白；这里按最亮的通道整体压缩，越密越亮，但颜色还是星自己的颜色。
 *
 * 入场用到的画面效果也在这一步：白光（uFlash）、画面撕裂（uGlitch，同原型）、
 * 爆炸冲击波（uShock）。原型的冲击波来自流体模拟，流体移植前先用向外扩散的扭曲圆环代替。
 */
import * as THREE from 'three';

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform sampler2D tScene;
uniform float uExposure;
uniform float uTime;
uniform float uFlash;
uniform float uGlitch;
uniform float uShock;
uniform vec2 uShockCenter;
uniform float uAspect;
varying vec2 vUv;

float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main() {
  vec2 uv = vUv, ab = vec2(0.0);
  if (uShock > 0.0 && uShock < 1.0) {
    // 冲击波：一圈向外推开画面的扭曲，越往外越弱
    vec2 d = uv - uShockCenter;
    d.x *= uAspect;
    float r = length(d), front = uShock * 1.7, w = 0.07 + 0.1 * uShock;
    float k = exp(-pow((r - front) / w, 2.0)) * pow(1.0 - uShock, 1.5);
    vec2 dir = r > 1e-4 ? d / r : vec2(0.0);
    dir.x /= uAspect;
    uv -= dir * k * 0.05;
    ab += dir * k * 0.012;
  }
  if (uGlitch > 0.0) {
    // 画面崩溃：横向错位的条带 + RGB 分离
    float tq = floor(uTime * 24.0), r1 = hash(vec2(floor(vUv.y * 42.0), tq));
    uv.x += (r1 - 0.5) * 0.09 * uGlitch * step(0.62, r1);
    ab += vec2(0.014 * uGlitch, 0.0);
  }
  vec3 c = dot(ab, ab) > 0.0
    ? vec3(texture2D(tScene, uv - ab).r, texture2D(tScene, uv).g, texture2D(tScene, uv + ab).b)
    : texture2D(tScene, uv).rgb;
  c *= uExposure;
  // 个别显卡上加法混合可能溢出成无穷大或 NaN，按黑处理，免得出现异色的点
  if (any(isnan(c)) || any(isinf(c))) c = vec3(0.0);
  c = max(c, vec3(0.0));
  float m = max(max(c.r, c.g), c.b);
  // 0.6 以下原样，以上平滑地压到 1，斜率连续
  float k = 0.6;
  float mm = m < k ? m : k + (1.0 - k) * (1.0 - exp(-(m - k) / (1.0 - k)));
  vec3 o = m > 1e-5 ? c * (mm / m) : c;
  // 特别密的地方稍微泛白，像星团的亮核
  o = mix(o, vec3(1.0), smoothstep(6.0, 40.0, m) * 0.35);
  o = min(o + uFlash * vec3(1.0, 0.92, 0.95), vec3(1.0));
  gl_FragColor = vec4(o, 1.0);
  #include <colorspace_fragment>
}
`;

export class ToneMapPass {
  readonly target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: true });
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  readonly uniforms = {
    tScene: { value: this.target.texture },
    uExposure: { value: 1 },
    uTime: { value: 0 },
    uFlash: { value: 0 },
    uGlitch: { value: 0 },
    uShock: { value: 0 },
    uShockCenter: { value: new THREE.Vector2(0.5, 0.5) },
    uAspect: { value: 1 },
  };

  constructor() {
    const g = new THREE.BufferGeometry();
    // 一个盖住全屏的大三角形
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    const quad = new THREE.Mesh(g, new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: this.uniforms, depthTest: false, depthWrite: false }));
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  /** 传绘制缓冲的像素尺寸 */
  setSize(width: number, height: number): void {
    this.target.setSize(width, height);
    this.uniforms.uAspect.value = width / Math.max(1, height);
  }

  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera): void {
    renderer.setRenderTarget(this.target);
    renderer.render(scene, camera);
    renderer.setRenderTarget(null);
    renderer.render(this.scene, this.camera);
  }
}
